import { useMemo, useState } from 'react';

// ───────────────────────────────────────────────────────── column model ──

export type SortType = 'string' | 'number' | 'boolean' | 'timeframe';
export type SortDir = 'asc' | 'desc';

export interface SortColumn<T> {
  /** Stable identifier for the column; what the sort state tracks. */
  key: string;
  /** Header label. */
  label: string;
  /** Pulls the comparable value out of a row. */
  accessor: (row: T) => unknown;
  /** How to compare values in this column. */
  type: SortType;
}

// ─────────────────────────────────────────────────────────── comparison ──

/**
 * Parse a timeframe string ("5m", "1h", "4h", "1d", "1w") into minutes so that
 * timeframes sort by real duration rather than lexically. Anything we don't
 * recognise sorts last (Infinity) regardless of direction handling below.
 */
export function tfToMinutes(tf: unknown): number {
  if (typeof tf !== 'string') return Infinity;
  const m = /^(\d+)\s*(m|h|d|w)$/i.exec(tf.trim());
  if (!m) return Infinity;
  const value = Number(m[1]);
  const unit = m[2].toLowerCase();
  const perUnit: Record<string, number> = { m: 1, h: 60, d: 1440, w: 10080 };
  return value * perUnit[unit];
}

/** Three-way comparison for a single column type. Nullish values sort last. */
function compareValues(a: unknown, b: unknown, type: SortType): number {
  switch (type) {
    case 'number': {
      const na = typeof a === 'number' ? a : Number.NEGATIVE_INFINITY;
      const nb = typeof b === 'number' ? b : Number.NEGATIVE_INFINITY;
      return na - nb;
    }
    case 'boolean': {
      // false < true
      return (a ? 1 : 0) - (b ? 1 : 0);
    }
    case 'timeframe': {
      return tfToMinutes(a) - tfToMinutes(b);
    }
    case 'string':
    default: {
      const sa = a == null ? '' : String(a);
      const sb = b == null ? '' : String(b);
      return sa.localeCompare(sb, undefined, { sensitivity: 'base', numeric: true });
    }
  }
}

// ───────────────────────────────────────────────────────────────── hook ──

export interface TableSort<T> {
  /** A sorted copy of `rows` (the input array is never mutated). */
  sorted: T[];
  /** Currently active column key, or null while in original order. */
  sortKey: string | null;
  sortDir: SortDir;
  /** Click handler: new column → ascending; same column → toggle direction. */
  sortBy: (key: string) => void;
}

/**
 * Client-side sorting for a small table. Sort state is local, so it survives
 * data refreshes (e.g. polling) and is applied to a fresh copy on every render.
 */
export function useTableSort<T>(rows: T[], columns: SortColumn<T>[]): TableSort<T> {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const sortBy = (key: string) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sorted = useMemo(() => {
    if (!sortKey) return rows;
    const col = columns.find((c) => c.key === sortKey);
    if (!col) return rows;
    const factor = sortDir === 'asc' ? 1 : -1;
    // Copy first: Array.prototype.sort is stable, so equal keys keep input order.
    return [...rows].sort((a, b) => factor * compareValues(col.accessor(a), col.accessor(b), col.type));
  }, [rows, columns, sortKey, sortDir]);

  return { sorted, sortKey, sortDir, sortBy };
}
